<template>
  <div>
    <Header>
      <template v-if="needBackButton" v-slot:left>
        <FeatherIcon
          icon="chevron-left"
          @click="routerBack"
          data-testid="backButton"
        />
      </template>
      振込口座
    </Header>

    <div class="set-bank pt-2 pb-4 mx-3">
      <StepBar
        v-if="needStepBar"
        :maxValue="4"
        :currentValue="3"
        class="mb-3"
        data-testid="stepBar"
      />
      <div v-else class="pb-3" />
      <CustomValidationObserver
        v-slot="{ handleSubmit }"
        :initialValues="formValues"
        ref="form"
      >
        <BankAccountForm v-model="inputValues" :banks="banks">
          <template #footerButton>
            <BottomFormActionButtons
              v-if="needBottomActionButtons"
              skipButtonText="スキップする"
              @submit="handleSubmit(onClickSubmitButton)"
              @skip="$emit('skipped')"
              data-testid="skipButton"
            />
            <AppButton
              v-else
              block
              class="mt-4"
              variant="success"
              @click="handleSubmit(onClickSubmitButton)"
              data-testid="submitButton"
              :disabled="isSubmittingDisabled"
              >設定する</AppButton
            >
          </template>
        </BankAccountForm>
      </CustomValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import AppButton from "@/components/atoms/button/AppButton.vue";
import AppLabel from "@/components/atoms/input/AppLabel.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import TextFieldGroup from "@/components/molecules/form/TextFieldGroup.vue";
import { accountService } from "@/app/services/AccountService";
import { salaryService } from "@/app/services/SalaryService";
import { authStore } from "@/store/auth";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { sendAdjustEvent } from "@/plugins/adjust";
import { sendFirebaseCustomEvent } from "@/plugins/firebase";
import BottomFormActionButtons from "@/components/organisms/register/BottomFormActionButtons.vue";
import BankAccountForm from "@/components/organisms/common/BankAccountForm.vue";
import { BankAccountQueueBody } from "@/types/bank_account/BankAccountQueueBody";
import StepBar from "@/components/molecules/other/StepBar.vue";
import { masterStore } from "@/store/master";
import CustomValidationObserver from "@/plugins/CustomValidationObserver.vue";

@Component({
  components: {
    AppButton,
    AppLabel,
    FeatherIcon,
    Header,
    TextFieldGroup,
    BottomFormActionButtons,
    BankAccountForm,
    StepBar
  }
})
export default class SetBankTemplate extends Vue {
  @Prop({
    type: Boolean,
    default: false
  })
  needStepBar!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  needBackButton!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  needBottomActionButtons!: boolean;

  isAlreadyRegistered = false;
  inputValues: BankAccountQueueBody = {
    selectedBank: null,
    searchedBanks: [],
    branchCode: "",
    branchName: "",
    accountNumber: "",
    holderName: ""
  };

  get formValues() {
    return {
      selectedBank: this.inputValues.selectedBank,
      branchCode: this.inputValues.branchCode,
      branchName: this.inputValues.branchName,
      accountNumber: this.inputValues.accountNumber,
      holderName: this.inputValues.holderName
    };
  }

  get banks() {
    return masterStore.banks;
  }

  get user() {
    return authStore.user!;
  }

  async created() {
    try {
      this.$loading.show();
      // アカウント情報を更新
      await accountService.updateUserData();
      await this.fetchBankAccount();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  mounted() {
    // 取得した値でフォームをリセットしておく
    // https://github.com/logaretm/vee-validate/issues/4602
    (this.$refs.form as InstanceType<
      typeof CustomValidationObserver
    >).resetForm(this.formValues);
  }

  async fetchBankAccount() {
    try {
      const bankAccount = await salaryService.fetchBankAccount();
      // 銀行口座アカウントが見つかったらフラグをtrueに
      this.isAlreadyRegistered = true;
      this.inputValues.selectedBank = this.banks.find(bank => {
        return bank.code === bankAccount.bankCode;
      })!;
      this.inputValues.branchCode = bankAccount.branchCode;
      this.inputValues.branchName = bankAccount.branchName;
      this.inputValues.accountNumber = bankAccount.accountNumber;
      this.inputValues.holderName = bankAccount.holderName;
    } catch {
      this.isAlreadyRegistered = false;
    }
  }

  async onClickSubmitButton() {
    if (!this.inputValues.selectedBank) {
      await this.$bvModal.msgBoxOk("銀行名を入力してください。", {
        centered: true
      });
      return;
    }
    if (!this.inputValues.branchName || !this.inputValues.branchCode) {
      await this.$bvModal.msgBoxOk("支店を入力してください。", {
        centered: true
      });
      return;
    }
    try {
      const body = {
        bankCode: this.inputValues.selectedBank!.code,
        branchCode: this.inputValues.branchCode,
        branchName: this.inputValues.branchName,
        accountNumber: this.inputValues.accountNumber
      };
      this.$loading.show();
      if (this.isAlreadyRegistered) {
        // 口座情報があれば更新
        await salaryService.updateBankAccount(body);
      } else {
        // 口座情報がなかったら作成
        await salaryService.createBankAccount(body);
      }
      sendAdjustEvent("FINISH_INPUT_BANK_ACCOUNT");
      sendFirebaseCustomEvent("finish_input_bank_account", {
        session_id: authStore.sessionId || "unknown"
      });
      this.$emit("updated");
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async routerBack() {
    await this.$router.back();
  }

  get isSubmittingDisabled(): boolean {
    return (
      !this.inputValues.selectedBank?.code ||
      !this.inputValues.branchCode ||
      !this.inputValues.branchName ||
      !this.inputValues.accountNumber
    );
  }
}
</script>
